/**
 * EventDetail コンポーネント (SC-05)
 *
 * イベント詳細ページ。個別イベントの詳細情報を表示。
 *
 */

import { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Header } from '@/components/organisms/Header';
import { Footer } from '@/components/organisms/Footer';
import { Badge } from '@/components/atoms/Badge';
import { useLanguageStore } from '@/stores/language-store';
import { useDataStore } from '@/stores/data-store';
import { useI18n } from '@/i18n/useI18n';
import { getLocalizedText } from '@/lib/i18n-helpers';
import { formatDate, formatDateRange } from '@/lib/date-formatters';

/**
 * カテゴリ名の翻訳
 */
/**
 * EventDetail コンポーネント
 *
 * 使用例:
 * ```tsx
 * <Route path="/events/:id" element={<EventDetail />} />
 * ```
 */
export function EventDetail() {
  const { id } = useParams<{ id: string }>();
  const [failedImage, setFailedImage] = useState<string>();
  const { language } = useLanguageStore();
  const {
    eventsLoaded,
    isLoadingEvents,
    eventsError,
    fetchEvents,
    findEventBySlug,
    worksLoaded,
    worksError,
    fetchWorks,
    findWorkBySlug,
  } = useDataStore();
  const { t } = useI18n();
  const routerLocation = useLocation();

  // ページ遷移時にスクロールを最上位にリセット
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  // データ読み込み（未ロードかつエラーなしの場合のみ）
  useEffect(() => {
    if (!eventsLoaded && !eventsError) fetchEvents();
  }, [eventsLoaded, eventsError, fetchEvents]);

  // slug からO(1)検索
  const event = id ? findEventBySlug(id) : undefined;

  // 出品作品表示に必要な場合のみ works を読み込む
  useEffect(() => {
    if (event?.workFeatured?.length && !worksLoaded && !worksError) {
      fetchWorks();
    }
  }, [event, worksLoaded, worksError, fetchWorks]);

  // 出品作品を算出
  const featuredWorks = useMemo(() => {
    if (!worksLoaded) return [];

    return (
      event?.workFeatured?.flatMap((wid) => {
        const w = findWorkBySlug(wid);
        return w ? [w] : [];
      }) ?? []
    );
  }, [event, worksLoaded, findWorkBySlug]);

  // 一覧ページへ戻るURLを生成（現在のフィルタ条件を保持）
  const backToListUrl = useMemo(() => {
    if (routerLocation.state && typeof routerLocation.state === 'object') {
      const from = (routerLocation.state as { from?: string }).from;
      if (from) {
        return from;
      }
    }
    return '/events';
  }, [routerLocation.state]);

  // ページ状態の判定
  const pageState = eventsError
    ? 'error'
    : !eventsLoaded || isLoadingEvents
      ? 'loading'
      : !event
        ? 'not-found'
        : 'ready';

  if (pageState !== 'ready') {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <main
          id="main-content"
          className="flex flex-1 items-center justify-center"
        >
          <div className="text-center">
            {pageState === 'loading' && (
              <>
                <div className="mb-4 text-4xl">⏳</div>
                <p className="text-muted-foreground">{t('common.loading')}</p>
              </>
            )}
            {pageState === 'error' && (
              <>
                <p className="text-lg font-medium">{t('common.loadError')}</p>
                <button
                  onClick={() => fetchEvents()}
                  className="mt-4 inline-block text-sm text-primary hover:underline"
                >
                  {t('common.retry')}
                </button>
              </>
            )}
            {pageState === 'not-found' && (
              <>
                <div className="mb-4 text-4xl">❌</div>
                <p className="text-lg font-medium">
                  {t('eventDetail.notFound')}
                </p>
                <Link
                  to="/events"
                  className="mt-4 inline-block text-sm text-primary hover:underline"
                >
                  {t('common.back')}
                </Link>
              </>
            )}
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // pageState === 'ready' guarantees event is defined
  if (!event) return null;

  const name = getLocalizedText(event.title, language);
  const description = event.description
    ? getLocalizedText(event.description, language)
    : '';
  const genre = event.eventType;
  const eventLocation = getLocalizedText(event.location || '', language);
  const imageKey = `${event.id}:${event.image || ''}`;

  // 日付フォーマット
  const dateDisplay = event.endDate
    ? formatDateRange(event.startDate, event.endDate, language)
    : formatDate(event.startDate, language);

  // カテゴリラベル
  return (
    <div className="flex min-h-screen flex-col">
      <Helmet>
        <title>
          {name} - {t('site.title')}
        </title>
        <meta name="description" content={description || name} />
      </Helmet>

      <Header />

      <main id="main-content" className="flex-1">
        {/* パンくずナビゲーション */}
        <section className="border-b bg-muted/20 py-3">
          <div className="container mx-auto px-4">
            <nav aria-label="Breadcrumb" className="text-sm">
              <ol className="flex items-center gap-2 text-muted-foreground">
                <li>
                  <Link to="/" className="hover:text-foreground">
                    {t('common.home')}
                  </Link>
                </li>
                <li>/</li>
                <li>
                  <Link to="/events" className="hover:text-foreground">
                    {t('eventsList.title')}
                  </Link>
                </li>
                <li>/</li>
                <li className="font-medium text-foreground">{name}</li>
              </ol>
            </nav>
          </div>
        </section>

        {/* 一覧へ戻るボタン */}
        <section className="border-b bg-background py-3">
          <div className="container mx-auto px-4">
            <Link
              to={backToListUrl}
              className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                className="h-4 w-4"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M17 10a.75.75 0 01-.75.75H5.612l4.158 3.96a.75.75 0 11-1.04 1.08l-5.5-5.25a.75.75 0 010-1.08l5.5-5.25a.75.75 0 111.04 1.08L5.612 9.25H16.25A.75.75 0 0117 10z"
                  clipRule="evenodd"
                />
              </svg>
              {t('common.back')}
            </Link>
          </div>
        </section>

        {/* イベント詳細 */}
        <section className="py-8">
          <div className="container mx-auto px-4">
            <div className="mx-auto max-w-4xl">
              {/* タイトル */}
              <h1 className="mb-6 text-3xl font-bold">{name}</h1>

              {/* 代表画像 */}
              {event.image && failedImage !== imageKey && (
                <div className="mb-8 overflow-hidden rounded-lg border bg-muted">
                  <img
                    key={imageKey}
                    src={event.image}
                    alt={name}
                    className="mx-auto h-auto max-h-[70vh] w-full object-contain"
                    width="640"
                    height="360"
                    onError={() => setFailedImage(imageKey)}
                  />
                </div>
              )}

              {/* メタ情報カード */}
              <div className="mb-8 rounded-lg border bg-card p-6">
                <div className="grid gap-4 sm:grid-cols-2">
                  {/* 開催日 */}
                  <div>
                    <div className="mb-1 text-sm font-semibold text-muted-foreground">
                      {t('eventDetail.labels.date')}
                    </div>
                    <div className="flex items-center gap-2">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        className="h-5 w-5 text-primary"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.75 2a.75.75 0 01.75.75V4h7V2.75a.75.75 0 011.5 0V4h.25A2.75 2.75 0 0118 6.75v8.5A2.75 2.75 0 0115.25 18H4.75A2.75 2.75 0 012 15.25v-8.5A2.75 2.75 0 014.75 4H5V2.75A.75.75 0 015.75 2zm-1 5.5c-.69 0-1.25.56-1.25 1.25v6.5c0 .69.56 1.25 1.25 1.25h10.5c.69 0 1.25-.56 1.25-1.25v-6.5c0-.69-.56-1.25-1.25-1.25H4.75z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span>{dateDisplay}</span>
                    </div>
                  </div>

                  {/* イベント種別 */}
                  {genre ? (
                    <div>
                      <div className="mb-1 text-sm font-semibold text-muted-foreground">
                        {t('eventDetail.labels.genre')}
                      </div>
                      <Link
                        to={`/events?genres=${encodeURIComponent(genre)}`}
                        className="inline-block"
                      >
                        <Badge
                          variant="outline"
                          className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors"
                        >
                          {genre}
                        </Badge>
                      </Link>
                    </div>
                  ) : null}

                  {/* 開催場所 */}
                  {eventLocation && (
                    <div className="sm:col-span-2">
                      <div className="mb-1 text-sm font-semibold text-muted-foreground">
                        {t('eventDetail.labels.location')}
                      </div>
                      <div className="flex items-center gap-2">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          className="h-5 w-5 text-primary"
                        >
                          <path
                            fillRule="evenodd"
                            d="M9.69 18.933l.003.001C9.89 19.02 10 19 10 19s.11.02.308-.066l.002-.001.006-.003.018-.008a5.741 5.741 0 00.281-.14c.186-.096.446-.24.757-.433.62-.384 1.445-.966 2.274-1.765C15.302 14.988 17 12.493 17 9A7 7 0 103 9c0 3.492 1.698 5.988 3.355 7.584a13.731 13.731 0 002.273 1.765 11.842 11.842 0 00.976.544l.062.029.018.008.006.003zM10 11.25a2.25 2.25 0 100-4.5 2.25 2.25 0 000 4.5z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span>{eventLocation}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* 解説（description） */}
              {description && (
                <div className="mb-8">
                  <h2 className="mb-3 text-xl font-semibold">
                    {t('eventDetail.labels.description')}
                  </h2>
                  <p className="leading-relaxed text-muted-foreground whitespace-pre-wrap">
                    {description}
                  </p>
                </div>
              )}

              {/* 備考（note） */}
              {event.note && (
                <div className="mb-8">
                  <h2 className="mb-3 text-xl font-semibold">
                    {t('eventDetail.labels.notes')}
                  </h2>
                  <p className="leading-relaxed text-muted-foreground whitespace-pre-wrap">
                    {getLocalizedText(event.note, language)}
                  </p>
                </div>
              )}

              {/* 出品作品 */}
              {featuredWorks.length > 0 && (
                <div className="mb-8">
                  <h2 className="mb-4 text-xl font-semibold">
                    {t('eventDetail.featuredWorks')}
                  </h2>
                  <ul className="space-y-2">
                    {featuredWorks.map((work) => (
                      <li key={work.id}>
                        <Link
                          to={`/works/${work.id}`}
                          state={{
                            from:
                              routerLocation.pathname + routerLocation.search,
                          }}
                          className="inline-flex items-baseline gap-3 text-primary hover:underline"
                        >
                          <span>{getLocalizedText(work.title, language)}</span>
                          {work.dateCreated && (
                            <span className="text-sm text-muted-foreground">
                              {work.dateCreated}
                            </span>
                          )}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
